import React, { FC, FocusEvent, ReactElement } from 'react'
import classNames from 'classnames'

import styles from './Input.module.css'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

export interface InputProps {
	onClick?: (evt: any) => void
	onStartAdornmentClick?: (evt: any) => void
	onEndAdornmentClick?: (evt: any) => void
	className?: string
	name?: string
	inputClassName?: string
	onChange?: (
		evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
	) => void
	onKeyDown?: (evt: any) => void
	onFocus?: (evt: any) => void
	onBlur?: (evt: any) => void
	onParentFocus?: (evt: any) => void
	onParentBlur?: (evt: any) => void
	onPaste?: (evt: React.ClipboardEvent) => void
	onTouchStart?: (evt: React.TouchEvent<HTMLInputElement | HTMLTextAreaElement>) => void
	onTouchEnd?: (evt: React.TouchEvent<HTMLInputElement | HTMLTextAreaElement>) => void
	onPointerDown?: (evt: React.PointerEvent<HTMLInputElement | HTMLTextAreaElement>) => void
	onPointerUp?: (evt: React.PointerEvent<HTMLInputElement | HTMLTextAreaElement>) => void
	// The wrapper is a button so adornment rows can take a click. A text field
	// inside role="button" does not open the iOS keyboard.
	plain?: boolean
	placeholder?: string
	value?: string | number
	type?: string
	min?: string
	max?: string
	step?: string
	autoFocus?: boolean
	disabled?: boolean
	startAdornment?: string | ReactElement
	startAdornmentClassName?: string
	endAdornment?: string | ReactElement
	endAdornmentClassName?: string
	error?: boolean
	multiline?: boolean
	autoCapitalize?: string
	autoCorrect?: string
	spellCheck?: boolean
	rows?: number
	inputRef?: any
	inputId?: string
	autocomplete?: string
}

const Input: FC<InputProps> = (props): ReactElement => {
	const {
		autoFocus,
		autocomplete,
		className,
		disabled,
		endAdornment,
		endAdornmentClassName,
		error,
		inputClassName,
		inputId,
		inputRef: inputElementRef,
		max,
		min,
		multiline,
		autoCapitalize,
		autoCorrect,
		spellCheck,
		name,
		onBlur,
		onChange,
		onClick,
		onEndAdornmentClick,
		onFocus,
		onKeyDown,
		onParentBlur,
		onParentFocus,
		onPaste,
		onPointerDown,
		onPointerUp,
		onStartAdornmentClick,
		onTouchEnd,
		onTouchStart,
		placeholder,
		plain,
		rows,
		startAdornment,
		startAdornmentClassName,
		step,
		type,
		value
	} = props

	const handleParentFocus = React.useCallback(
		(e: FocusEvent) => {
			e.preventDefault()
			e.stopPropagation()
			onParentFocus && onParentFocus(e)
		},
		[onParentFocus]
	)

	const handleParentBlur = React.useCallback(
		(e: FocusEvent) => {
			e.preventDefault()
			e.stopPropagation()
			if (e.relatedTarget === null) {
				onParentBlur && onParentBlur(e)
			}
		},
		[onParentBlur]
	)

	const buttonWrapper = !plain

	return (
		<div
			onClick={onClick}
			onFocus={buttonWrapper ? handleParentFocus : undefined}
			onBlurCapture={buttonWrapper ? handleParentBlur : undefined}
			className={classNames(styles.root, { [className ?? '']: !!className })}
			role={buttonWrapper ? 'button' : undefined}
			tabIndex={buttonWrapper ? 0 : undefined}
			onKeyDown={buttonWrapper ? onKeyboardClick(onClick) : undefined}
		>
			{startAdornment && (
				<div
					onClick={onStartAdornmentClick}
					className={classNames(styles.adornment, styles.startAdornment, {
						[startAdornmentClassName ?? '']: !!startAdornmentClassName
					})}
					role="button"
					tabIndex={0}
					onKeyDown={onKeyboardClick(onStartAdornmentClick)}
				>
					{startAdornment}
				</div>
			)}
			{!multiline && (
				<input
					tabIndex={0}
					onChange={onChange}
					className={classNames(styles.input, {
						[inputClassName ?? '']: !!inputClassName,
						[styles.disabled]: disabled,
						[styles.startAdornmentInput]: !!startAdornment,
						[styles.endAdornmentInput]: !!endAdornment,
						[styles.error]: !!error
					})}
					onKeyDown={onKeyDown}
					autoFocus={autoFocus}
					onFocus={onFocus}
					onBlur={onBlur}
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
					onPointerDown={onPointerDown}
					onPointerUp={onPointerUp}
					disabled={disabled}
					max={max}
					min={min}
					placeholder={placeholder}
					autoCapitalize={autoCapitalize}
					autoCorrect={autoCorrect}
					spellCheck={spellCheck}
					step={step}
					type={type}
					value={value}
					onPaste={onPaste}
					id={inputId}
					name={name}
					ref={inputElementRef}
					autoComplete={autocomplete || 'off'}
				/>
			)}
			{multiline && (
				<textarea
					tabIndex={0}
					onChange={onChange}
					className={classNames(styles.input, {
						[inputClassName ?? '']: !!inputClassName,
						[styles.disabled]: disabled,
						[styles.startAdornmentInput]: !!startAdornment,
						[styles.endAdornmentInput]: !!endAdornment
					})}
					onKeyDown={onKeyDown}
					onFocus={onFocus}
					onBlur={onBlur}
					autoFocus={autoFocus}
					disabled={disabled}
					placeholder={placeholder}
					autoCapitalize={autoCapitalize}
					autoCorrect={autoCorrect}
					spellCheck={spellCheck}
					rows={rows}
					value={value}
					onPaste={onPaste}
					id={inputId}
					ref={inputElementRef}
					autoComplete={autocomplete || 'off'}
				/>
			)}
			{endAdornment && (
				<div
					tabIndex={0}
					onClick={onEndAdornmentClick}
					className={classNames(styles.adornment, styles.endAdornment, {
						[endAdornmentClassName ?? '']: !!endAdornmentClassName
					})}
					role="button"
					onKeyDown={onKeyboardClick(onEndAdornmentClick)}
				>
					{endAdornment}
				</div>
			)}
		</div>
	)
}

export default Input
